import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { fmtINR } from "@/lib/leadUtils";

const METHODS = ["UPI", "Bank transfer", "Cash", "Cheque", "Other"];
const LABEL_OPTIONS = [
  { value: "advance", label: "Advance" },
  { value: "mid", label: "Mid" },
  { value: "balance", label: "Balance" },
  { value: "extra", label: "Extra" },
];

const todayIso = () => new Date().toISOString().slice(0, 10);

export default function RecordPaymentModal({ project, onClose, onRecorded }) {
  const summary = project.payment_summary || {};
  const nextDue = summary.next_due_installment;

  const [installmentLabel, setInstallmentLabel] = useState(nextDue?.label || "balance");
  const [amount, setAmount] = useState(nextDue?.expected_amount || summary.total_outstanding || 0);
  const [method, setMethod] = useState("UPI");
  const [reference, setReference] = useState("");
  const [receivedOn, setReceivedOn] = useState(todayIso());
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // If user changes the installment label to one in the schedule, prefill expected amount.
  useEffect(() => {
    const inst = (summary.schedule || []).find((s) => s.label === installmentLabel);
    if (inst) {
      const paid = (summary.paid_by_label || {})[installmentLabel] || 0;
      const remaining = Math.max(inst.expected_amount - paid, 0);
      if (remaining > 0) setAmount(remaining);
    }
    // eslint-disable-next-line
  }, [installmentLabel]);

  // Detect partial — if amount differs from expected for the selected label,
  // suggest auto-relabel to "extra".
  const expectedForLabel = useMemo(() => {
    const inst = (summary.schedule || []).find((s) => s.label === installmentLabel);
    if (!inst) return null;
    const paid = (summary.paid_by_label || {})[installmentLabel] || 0;
    return Math.max(inst.expected_amount - paid, 0);
  }, [installmentLabel, summary]);

  const isPartial = expectedForLabel != null && amount > 0 && amount < expectedForLabel;

  const displayName = project.client_name + (project.partner_name ? ` × ${project.partner_name}` : "");

  const submit = async () => {
    setError("");
    if (!amount || amount <= 0) { setError("Amount must be greater than 0."); return; }
    if (!receivedOn) { setError("Received on date is required."); return; }
    setSubmitting(true);
    try {
      await api.post("/transactions", {
        project_id: project.id,
        type: "payment_received",
        installment_label: installmentLabel,
        amount: Number(amount),
        method,
        reference: reference || null,
        received_on: receivedOn,
        notes: notes || null,
      });
      toast.success(`Payment of ${fmtINR(amount)} recorded for ${displayName}`);
      onRecorded && onRecorded();
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not record payment.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(14,48,48,0.5)" }}
      data-testid="record-payment-modal"
    >
      <div className="bg-white p-6 w-[min(520px,94vw)]" style={{ borderRadius: 0 }} onClick={(e) => e.stopPropagation()}>
        <div className="kicker mb-3">RECORD PAYMENT</div>
        <h3 className="font-display text-xl mb-4" style={{ color: "var(--brand-ink)" }} data-testid="record-payment-title">
          {displayName}
        </h3>

        <div className="grid grid-cols-3 gap-3 mb-5 pb-4 border-b border-brand-border">
          <Mini label="Project total" value={fmtINR(project.total_quote_value)} />
          <Mini label="Collected" value={fmtINR(summary.total_collected || 0)} />
          <Mini label="Outstanding" value={fmtINR(summary.total_outstanding || 0)} highlight />
        </div>

        <div className="space-y-3">
          <Field label="Installment">
            <select
              value={installmentLabel}
              onChange={(e) => setInstallmentLabel(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-brand-border"
              style={{ borderRadius: 0 }}
              data-testid="record-payment-label"
            >
              {LABEL_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </Field>

          <Field label="Amount (INR)">
            <input
              type="number"
              min={1}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-brand-border"
              style={{ borderRadius: 0 }}
              data-testid="record-payment-amount"
            />
            {isPartial && (
              <div className="text-[11px] mt-1" style={{ color: "var(--brand-champagne)" }} data-testid="record-payment-partial-hint">
                Partial payment — expected {fmtINR(expectedForLabel)} for this installment. Add a note explaining the partial.
              </div>
            )}
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Method">
              <select
                value={method}
                onChange={(e) => setMethod(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-brand-border"
                style={{ borderRadius: 0 }}
                data-testid="record-payment-method"
              >
                {METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </Field>
            <Field label="Received on">
              <input
                type="date"
                value={receivedOn}
                onChange={(e) => setReceivedOn(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-brand-border"
                style={{ borderRadius: 0 }}
                data-testid="record-payment-date"
              />
            </Field>
          </div>

          <Field label="Reference (optional)">
            <input
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="UPI ref / Cheque no / Bank ref"
              className="w-full px-3 py-2 text-sm border border-brand-border"
              style={{ borderRadius: 0 }}
              data-testid="record-payment-reference"
            />
          </Field>

          <Field label={isPartial ? "Notes (required for partial payment)" : "Notes (optional)"}>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Optional context"
              className="w-full px-3 py-2 text-sm border border-brand-border"
              style={{ borderRadius: 0 }}
              data-testid="record-payment-notes"
            />
          </Field>
        </div>

        {error && <p className="text-xs text-rose-700 mt-3" data-testid="record-payment-error">{error}</p>}

        <div className="flex justify-end gap-2 mt-5">
          <button
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 text-xs tracking-[0.18em] uppercase text-brand-muted"
            data-testid="record-payment-cancel"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={submitting || !amount}
            className="px-4 py-2 text-xs tracking-[0.18em] uppercase text-white disabled:opacity-50"
            style={{ background: "var(--brand-primary)" }}
            data-testid="record-payment-submit"
          >
            {submitting ? "Recording…" : "Record payment"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="kicker block mb-1.5">{label}</span>
      {children}
    </label>
  );
}

function Mini({ label, value, highlight }) {
  return (
    <div>
      <div className="kicker mb-1">{label}</div>
      <div className="text-sm font-medium" style={{ color: highlight ? "var(--brand-champagne)" : "var(--brand-ink)" }}>
        {value}
      </div>
    </div>
  );
}
